import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {UtilUi} from '@common/utils-ui'
import {
  keyboardHandler,
  noPropagate,
  useClickAway,
  useGlobalKeyDown
} from '@containers/hooks'

// Used `textarea` and not `input` b/c Safari incorrectly calculates `input`
// height from 17px line-height somewhere which results in jitter when entering
// title edit mode
const TwEditableTitle = tw.textarea`
  !inline-block
  !outline-none
  !border-none
  !shadow-none
  !resize-none
  !overflow-hidden
  !box-content
  !m-0
  !p-0
  !bg-transparent
  cursor-text
  whitespace-nowrap
`

const textStyle: Partial<CSSStyleDeclaration> = {
  whiteSpace: 'pre',
  fontWeight: 'normal',
  fontSize: '14px',
  lineHeight: '16px'
}

const events = ['mousedown', 'touchstart']

export const EditableTitle = ({
  title,
  onSubmit,
  placeholder,
  className
}: {
  title: string
  onSubmit: (title?: string) => void
  placeholder?: string
  className?: string
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [localTitle, setLocalTitle] = useState(title)
  useEffect(() => {
    setLocalTitle(title)
  }, [title, setLocalTitle])

  const save = () => onSubmit(localTitle)
  const cancel = () => {
    onSubmit()
    return true
  }
  useClickAway(inputRef, save, events, true)
  useGlobalKeyDown({escape: cancel})

  const [editingWidth, setEditingWidth] = useState(0)
  useLayoutEffect(() => {
    const {width} = UtilUi.calcTextBounds(localTitle, textStyle)
    if (placeholder) {
      const {width: minWidth} = UtilUi.calcTextBounds(
        Lang.context.board.titlePlaceholder,
        textStyle
      )
      setEditingWidth(Math.max(width, minWidth))
    } else {
      setEditingWidth(width)
    }
  }, [placeholder, localTitle])

  // Manual focus on mount b/c autofocus conflicts w/ tab scroll
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus()
    })
  }, [inputRef])

  // `noPropagate` is required to prevent `preventDefault` on tab mousedown
  return (
    <TwEditableTitle
      ref={inputRef}
      style={{width: editingWidth}}
      className={className}
      onFocus={e =>
        e.target.setSelectionRange(e.target.value.length, e.target.value.length)
      }
      onBlur={save}
      onKeyDown={keyboardHandler({
        enter: save,
        escape: cancel
      })}
      onMouseDown={noPropagate}
      onClick={noPropagate}
      value={localTitle}
      onChange={e => setLocalTitle(e.target.value)}
      placeholder={placeholder}
    />
  )
}
